import { Button } from './Button.js';
import { GONE, REFUSED, SESSION_ENDED } from './resultWording.js';

/**
 * 064: the one place a read that did not produce content is put into words.
 *
 * Screens supply the domain noun for `gone` — only the screen knows whether a car or a booking
 * was being read — and nothing else. Every other word comes from `resultWording.ts`, which a
 * supporting lookup's inline message reads from too, so the same situation cannot be described two
 * ways on two screens (FR-011).
 *
 * A retry is offered for `failed` and never for `gone`, `unauthenticated` or `refused`: retrying
 * a 404, an ended session or a refusal cannot succeed, and a control that invites an attempt
 * certain to fail is the defect I5 was fixed for on the cancel path.
 */
interface ResultStateProps {
  /** The read's outcome. Anything other than the four refusal states renders nothing. */
  state: { status: string; reason?: string };
  onRetry?: () => void;
  /** What is gone, in the screen's own words. */
  goneMessage?: string;
  onwardLabel?: string;
  onOnward?: () => void;
  onSignIn?: () => void;
}

export function ResultState({
  state,
  onRetry,
  goneMessage,
  onwardLabel = 'Back to search',
  onOnward,
  onSignIn,
}: ResultStateProps) {
  // idle, loading and ok belong to the screen — a spinner, the content, or the screen's own
  // empty copy. Rendering them here would put this component in charge of what "no results"
  // means, which is exactly the confusion the feature removes.
  if (state.status === 'idle' || state.status === 'loading' || state.status === 'ok') return null;

  if (state.status === 'gone') {
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <p className="text-center text-error">{goneMessage ?? GONE}</p>
        {onOnward && (
          <Button variant="ghost" onClick={onOnward}>
            {onwardLabel}
          </Button>
        )}
      </div>
    );
  }

  if (state.status === 'unauthenticated') {
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <p className="text-center text-error">{SESSION_ENDED}</p>
        {onSignIn && (
          <Button variant="ghost" onClick={onSignIn}>
            Sign in
          </Button>
        )}
      </div>
    );
  }

  if (state.status === 'refused') {
    return (
      <div className="flex flex-col items-center gap-3 py-16">
        <p className="text-center text-error">{REFUSED}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 py-16">
      <p className="text-center text-error">{state.reason}</p>
      {onRetry && (
        <Button variant="ghost" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}
